import React, { useEffect, useState } from "react";
import { getUsersDirectory } from "../../../../api/api.js";
import { normalizeIsFired } from "../../../../utils/buildUserUpdatePayload";
import { formatGenderLabel } from "../Register/GenderSelect";
import Preloader from "../../../Preloader/Preloader.js";
import Select from "react-select";
import "./Users.css";

const getFiredStatusLabel = (user) =>
  normalizeIsFired(user.is_fired) ? "Уволен" : "Не уволен";

const getUserDisplayName = (user) => user.full_name?.trim() || "";

const sortDirectoryUsers = (users = []) =>
  [...users].sort((a, b) =>
    getUserDisplayName(a).localeCompare(getUserDisplayName(b), "ru", {
      sensitivity: "base",
    }),
  );

const userMatchesFilter = (user, key, values) => {
  if (values.length === 0) return true;

  if (key === "name") {
    const fullName = getUserDisplayName(user).toLowerCase();
    return values.some((value) => fullName.includes(value.toLowerCase()));
  }
  if (key === "department") {
    return values.some((value) =>
      user.group?.toLowerCase().includes(value.toLowerCase()),
    );
  }
  if (key === "position") {
    return values.some((value) =>
      user.position?.toLowerCase().includes(value.toLowerCase()),
    );
  }
  if (key === "status") {
    return values.includes(getFiredStatusLabel(user));
  }
  if (key === "gender") {
    return values.includes(formatGenderLabel(user.gender));
  }

  return values.some((value) =>
    user[key]?.toString().toLowerCase().includes(value.toLowerCase()),
  );
};

const formatFiredStatus = (user) => {
  const label = getFiredStatusLabel(user);
  if (label === "Не уволен" || !user.is_fired_date) return label;
  const date = String(user.is_fired_date).split("T")[0].substring(0, 10);
  const [y, m, d] = date.split("-");
  return y && m && d ? `${label} (${d}.${m}.${y})` : label;
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blurStates, setBlurStates] = useState({});
  const [filters, setFilters] = useState({
    name: [],
    department: [],
    position: [],
    email: [],
    username: [],
    status: [],
    gender: [],
  });
  const [filteredUsers, setFilteredUsers] = useState([]);

  useEffect(() => {
    const getUsers = async () => {
      try {
        const data = sortDirectoryUsers(await getUsersDirectory());
        setUsers(data);
        setFilteredUsers(data);
        const initialBlurStates = data.reduce((acc, user) => {
          acc[user.id] = true;
          return acc;
        }, {});
        setBlurStates(initialBlurStates);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    getUsers();
  }, []);

  useEffect(() => {
    const filtered = users.filter((user) =>
      Object.entries(filters).every(([key, values]) =>
        userMatchesFilter(user, key, values),
      ),
    );
    setFilteredUsers(filtered);
  }, [filters, users]);

  const getUsersForColumnOptions = (column, nextFilters = filters) =>
    users.filter((user) =>
      Object.entries(nextFilters).every(([key, values]) => {
        if (key === column) return true;
        return userMatchesFilter(user, key, values);
      }),
    );

  const toggleBlur = (userId) => {
    setBlurStates((prevState) => ({
      ...prevState,
      [userId]: !prevState[userId],
    }));
  };

  const formatSalary = (salary) => {
    if (!salary) return "Не указано";
    return salary.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "Не указано";
    return phoneNumber.replace(
      /(\+\d)(\d{3})(\d{3})(\d{2})(\d{2})/,
      "$1 ($2) $3-$4-$5"
    );
  };

  const getUniqueValues = (key, sourceUsers = users) => {
    const values = [
      ...new Set(
        sourceUsers
          .flatMap((user) => {
            if (key === "name") return getUserDisplayName(user);
            if (key === "department") return user.group;
            if (key === "position") return user.position;
            if (key === "status") return getFiredStatusLabel(user);
            if (key === "gender") return formatGenderLabel(user.gender);
            return user[key]?.toString();
          })
          .filter(Boolean),
      ),
    ];

    return values.sort((a, b) =>
      String(a).localeCompare(String(b), "ru", { sensitivity: "base" }),
    );
  };

  const handleFilterChange = (column, selectedOptions) => {
    const newValues = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];

    setFilters((prev) => {
      const next = { ...prev, [column]: newValues };

      if (column === "department") {
        const availablePositions = new Set(
          getUniqueValues("position", getUsersForColumnOptions("position", next)),
        );
        next.position = prev.position.filter((position) =>
          availablePositions.has(position),
        );
      }

      if (column === "position") {
        const availableDepartments = new Set(
          getUniqueValues(
            "department",
            getUsersForColumnOptions("department", next),
          ),
        );
        next.department = prev.department.filter((department) =>
          availableDepartments.has(department),
        );
      }

      return next;
    });
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      padding: "5px",
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
      lineHeight: "19.12px",
      width: "100%",
      minWidth: 0,
    }),
    menu: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      marginTop: "5px",
      zIndex: 9999,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
    option: (provided, state) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
    }),
    multiValue: (provided, state) => ({
      ...provided,
      backgroundColor: "#E0E0E0",
      borderRadius: "5px",
    }),
    multiValueLabel: (provided, state) => ({
      ...provided,
      fontSize: "12px",
    }),
    multiValueRemove: (provided, state) => ({
      ...provided,
      color: "#666",
      ":hover": {
        backgroundColor: "#ccc",
        color: "#333",
      },
    }),
  };

  const getFilterLabel = (column) => {
    const labels = {
      name: "ФИО",
      department: "Отдел",
      position: "Должность",
      email: "E-mail",
      status: "Увольнение",
      gender: "Пол",
    };
    return labels[column] || column;
  };

  const renderFilterInput = (column) => {
    if (["salary", "birth_date", "phone_number"].includes(column)) {
      return getFilterLabel(column);
    }

    const options = getUniqueValues(
      column,
      getUsersForColumnOptions(column),
    ).map((value) => ({
      value: value,
      label: value,
    }));

    return (
      <Select
        isMulti
        options={options}
        styles={customStyles}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        value={filters[column].map((value) => ({ value, label: value }))}
        onChange={(selectedOptions) =>
          handleFilterChange(column, selectedOptions)
        }
        placeholder={getFilterLabel(column)}
        noOptionsMessage={() => "Нет доступных опций"}
      />
    );
  };

  if (loading) {
    return <Preloader />;
  }

  if (error) {
    return <div>Ошибка: {error}</div>;
  }

  return (
    <div className="users-container">
      <h1>Все пользователи</h1>
      <div className="users-table-wrapper">
        <table className="users">
          <thead>
            <tr>
              <th>{renderFilterInput("name")}</th>
              <th>{renderFilterInput("department")}</th>
              <th>{renderFilterInput("position")}</th>
              <th>{renderFilterInput("email")}</th>
              <th>{renderFilterInput("gender")}</th>
              <th>{renderFilterInput("status")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr className="users__points" key={user.id}>
                <td className="users__point">
                  {getUserDisplayName(user) || "Не указано"}
                </td>
                <td className="users__point">{user.group || "Не указано"}</td>
                <td className="users__point">
                  {user.position || "Не указано"}
                </td>
                <td className="users__point">{user.email || "Не указано"}</td>
                <td className="users__point">
                  {formatGenderLabel(user.gender)}
                </td>
                <td className="users__point">{formatFiredStatus(user)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="users-total">
        Итого: {filteredUsers.length} сотрудников
      </div>
    </div>
  );
};

export default Users;
